import React from 'react';
import { gql, TypedDocumentNode } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { hrefFor } from '../../routers';
import { PageLayoutQuery, PageLayoutQueryVariables } from '../../generated/types/PageLayoutQuery';
import { usePreserveUtmQueryParam } from '../../hooks/usePreserveUtmQueryParam';
import { useSessionQueryParams } from '../../hooks/useSessionQueryParams';
import { usePageTracking } from '../../hooks/usePageTracking';
import { useHandleBambuserRedirects } from '../../hooks/useHandleBambuserRedirects';
import { useSignifydSessionId } from '../../hooks/useSignifydSessionId';
import { usePreservePromoCodeInCookie } from '../../hooks/usePreservePromoCodeInCookie';
import { useSkippableQuery } from '../../hooks/useSkippableQuery';
import { EnableLazyLoad } from '../LazyLoad';
import { useCountry } from '../CountrySelector';
import { ErrorBoundary } from '../ErrorBoundary';
import { SitePromoBanner, SITE_PROMO_BANNER_FRAGMENT } from '../SitePromoBanner';
import { SiteNav, SITE_NAV_FRAGMENT } from '../SiteNav';
import { MinimalNav } from '../MinimalNav';
import { Notifications } from '../Notifications';
import { SiteFooter } from '../SiteFooter';
import { EmailSignup } from '../EmailSignup';

import './PageLayout.scss';

export const PAGE_LAYOUT_QUERY: TypedDocumentNode<PageLayoutQuery, PageLayoutQueryVariables> = gql`
  query PageLayoutQuery($gender: String!, $slug: String!, $countryCode: String!) {
    displayPage(gender: $gender, slug: $slug, country_code: $countryCode) {
      ...SitePromoBannerFragment
      ...SiteNavFragment
    }
  }
  ${SITE_NAV_FRAGMENT}
  ${SITE_PROMO_BANNER_FRAGMENT}
`;

export const PageLayout: React.FC = ({ children }) => {
  const {
    country: { alpha2Code: countryCode }
  } = useCountry();
  const { pathname } = useLocation();
  const includeHeader = pathname !== hrefFor.CheckoutPage();

  const { data, error } = useSkippableQuery(PAGE_LAYOUT_QUERY, {
    variables: { gender: 'women', slug: 'women', countryCode },
    skip: !includeHeader
  });

  if (error) {
    // Allow page to render as best it can and warn the error
    console.warn(error.message); // eslint-disable-line no-console
  }

  usePreserveUtmQueryParam();
  useSessionQueryParams();
  usePageTracking();
  useHandleBambuserRedirects();
  useSignifydSessionId({ createIfNotExists: true });
  usePreservePromoCodeInCookie();

  return (
    <div className="PageLayout">
      {includeHeader && (
        <ErrorBoundary FallbackComponent={MinimalNav}>
          <SitePromoBanner displayPage={data?.displayPage} />
          <EnableLazyLoad>
            <SiteNav displayPage={data?.displayPage} />
          </EnableLazyLoad>
        </ErrorBoundary>
      )}

      <EmailSignup />
      <Notifications />

      <main className="PageLayout__content">{children}</main>

      <SiteFooter />
    </div>
  );
};
